import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft, RefreshCw, ExternalLink, Heart, MessageCircle, Play, Eye,
  Loader2, Save, Pencil, Flame, Image as ImageIcon, FileText, CalendarClock,
  Plus, Trash2, Clock,
  type LucideIcon,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import {
  getAccount, getAccountMetrics, getAccountMedia, syncAccount, updateAccount,
  fetchMediaThumb, listWarmup, createWarmup, deleteWarmup,
  type AccountDetail, type AccountMetricPoint, type AccountMedia, type AccountStatus,
  type WarmupSession,
} from "../api/accounts";
import { StatusBadge } from "../components/StatusBadge";
import { AccountAvatar } from "../components/AccountAvatar";
import { SyncBadge } from "./AccountsPage";
import { useRealtimeChannel } from "../hooks/useRealtimeChannel";
import { formatDateTime, relativeTime } from "../utils/format";

type Range = "7d" | "30d" | "90d" | "all";

function formatCount(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

export function AccountDetailPage(): JSX.Element {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [account, setAccount] = useState<AccountDetail | null>(null);
  const [metrics, setMetrics] = useState<AccountMetricPoint[]>([]);
  const [media, setMedia] = useState<AccountMedia[]>([]);
  const [warmup, setWarmup] = useState<WarmupSession[]>([]);
  const [range, setRange] = useState<Range>("30d");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  const loadAccount = useCallback(async () => {
    try {
      setError(null);
      const acc = await getAccount(id);
      setAccount(acc);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("accounts.detail.notFound"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  const loadMetrics = useCallback(async () => {
    try {
      setMetrics(await getAccountMetrics(id, range));
    } catch { /* noop */ }
  }, [id, range]);

  const loadMedia = useCallback(async () => {
    try {
      const r = await getAccountMedia(id, { limit: 60 });
      setMedia(r.media);
    } catch { /* noop */ }
  }, [id]);

  const loadWarmup = useCallback(async () => {
    try {
      setWarmup(await listWarmup(id));
    } catch { /* noop */ }
  }, [id]);

  useEffect(() => { void loadAccount(); void loadMedia(); void loadWarmup(); }, [loadAccount, loadMedia, loadWarmup]);
  useEffect(() => { void loadMetrics(); }, [loadMetrics]);

  // Temps réel : sync terminé → recharge.
  const rooms = useMemo(() => [`account:${id}`], [id]);
  const onEvent = useCallback((evt: { event: string }) => {
    if (evt.event === "account:metrics") {
      void loadAccount(); void loadMetrics(); void loadMedia();
    }
  }, [loadAccount, loadMetrics, loadMedia]);
  useRealtimeChannel(rooms, onEvent);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setSyncError(null);
    try {
      await syncAccount(id);
      // Recharge compteurs + courbe + grille (l'event temps réel le fait aussi,
      // mais on rafraîchit ici au cas où le canal realtime serait indisponible).
      await Promise.all([loadAccount(), loadMetrics(), loadMedia()]);
    } catch (e) {
      setSyncError(e instanceof Error ? e.message : t("accounts.actions.syncFailed"));
    } finally {
      setSyncing(false);
    }
  }, [id, loadAccount, loadMetrics, loadMedia, t]);

  // Auto-sync au montage si on arrive juste après une création de compte
  // (navigate(..., { state: { autosync: true } })). On nettoie l'historique
  // pour qu'un refresh ne relance pas la synchro.
  const didAutoSync = useRef(false);
  useEffect(() => {
    if (didAutoSync.current) return;
    const st = location.state as { autosync?: boolean } | null;
    if (st?.autosync) {
      didAutoSync.current = true;
      navigate(location.pathname, { replace: true, state: {} });
      void handleSync();
    }
  }, [location.state, location.pathname, navigate, handleSync]);

  const chartData = useMemo(
    () => metrics.map((m) => ({
      date: shortDate(m.fetched_at),
      followers: m.followers,
      following: m.following,
      posts: m.posts_count,
    })),
    [metrics]
  );

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="size-8 text-velvet-500 animate-spin" />
      </div>
    );
  }

  if (error || !account) {
    return (
      <div className="p-8">
        <button onClick={() => navigate("/accounts")} className="inline-flex items-center gap-2 text-ink-500 hover:text-white mb-4">
          <ArrowLeft className="size-4" /> {t("accounts.detail.back")}
        </button>
        <div className="p-4 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
          {error || t("accounts.detail.notFound")}
        </div>
      </div>
    );
  }

  const profileUrl = `https://instagram.com/${account.ig_username}`;

  return (
    <div className="p-8 space-y-6">
      <button onClick={() => navigate("/accounts")} className="inline-flex items-center gap-2 text-ink-500 hover:text-white">
        <ArrowLeft className="size-4" /> {t("accounts.detail.back")}
      </button>

      {/* Bannière sync : chargement en cours puis erreur + retry */}
      {syncing && (
        <div className="p-4 rounded-lg border border-velvet-500/30 bg-velvet-500/10 text-velvet-200 text-sm flex items-center gap-3">
          <Loader2 className="size-4 animate-spin shrink-0" />
          {t("accounts.detail.syncInProgress")}
        </div>
      )}
      {!syncing && syncError && (
        <div className="p-4 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm flex items-center justify-between gap-3">
          <span>{t("accounts.detail.syncErrorPrefix")} : {syncError}</span>
          <button
            onClick={() => void handleSync()}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-red-500/40 hover:bg-red-500/20 text-red-200 text-sm font-medium shrink-0"
          >
            <RefreshCw className="size-4" /> {t("accounts.actions.retrySync")}
          </button>
        </div>
      )}

      {/* Header profil */}
      <div className="bg-ink-800 border border-ink-700 rounded-xl p-6">
        <div className="flex flex-wrap items-start gap-5">
          <AccountAvatar accountId={account.id} username={account.ig_username} hasPic={!!account.profile_pic_path} size={88} />
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold">@{account.ig_username}</h1>
              <StatusBadge status={account.status} />
              <SyncBadge status={account.sync_status} />
              <a href={profileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-velvet-400 hover:text-velvet-300">
                <ExternalLink className="size-3" /> {t("accounts.actions.openProfile")}
              </a>
            </div>
            {account.full_name && <div className="text-ink-300 mt-1">{account.full_name}</div>}
            <p className="text-sm text-ink-500 mt-1 whitespace-pre-wrap">
              {account.biography || t("accounts.detail.bioPlaceholder")}
            </p>
            <div className="flex gap-6 mt-4">
              <Stat label={t("accounts.detail.followers")} value={formatCount(account.followers)} />
              <Stat label={t("accounts.detail.following")} value={formatCount(account.following)} />
              <Stat label={t("accounts.detail.posts")} value={formatCount(account.posts_count)} />
            </div>
            {account.last_synced_at && (
              <div className="text-xs text-ink-500 mt-3">
                {t("accounts.detail.lastSync")} : {relativeTime(account.last_synced_at)}
              </div>
            )}
          </div>
          <button
            onClick={() => void handleSync()}
            disabled={syncing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 text-white text-sm font-medium disabled:opacity-50"
          >
            <RefreshCw className={`size-4 ${syncing ? "animate-spin" : ""}`} />
            {t("accounts.actions.syncNow")}
          </button>
        </div>
      </div>

      {/* Stats agrégées */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <DetailStatCard icon={ImageIcon} label={t("accounts.detail.statPosts")} value={formatCount(account.posts_count ?? account.media_count)} accent="velvet" />
        <DetailStatCard icon={Eye} label={t("accounts.detail.statViews")} value={formatCount(account.total_views)} accent="sky" />
        <DetailStatCard icon={Heart} label={t("accounts.detail.statLikes")} value={formatCount(account.total_likes)} accent="emerald" />
        <DetailStatCard icon={Flame} label={t("accounts.detail.statWarmup")} value={formatHours(account.warmup_minutes)} accent="amber" />
      </div>

      {/* Courbes */}
      <div className="bg-ink-800 border border-ink-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{t("accounts.detail.overview")}</h2>
          <div className="flex gap-1">
            {(["7d", "30d", "90d", "all"] as Range[]).map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-3 py-1 rounded-lg text-xs ${range === r ? "bg-velvet-600 text-white" : "border border-ink-700 text-ink-500 hover:text-white"}`}
              >
                {t(`accounts.range.${r === "7d" ? "d7" : r === "30d" ? "d30" : r === "90d" ? "d90" : "all"}`)}
              </button>
            ))}
          </div>
        </div>
        {chartData.length === 0 ? (
          <div className="py-12 text-center text-ink-500 text-sm">{t("accounts.detail.noMetrics")}</div>
        ) : (
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a35" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8a8a99" }} />
                <YAxis tick={{ fontSize: 11, fill: "#8a8a99" }} />
                <Tooltip contentStyle={{ background: "#1a1a22", border: "1px solid #2a2a35", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="followers" name={t("accounts.detail.followers")} stroke="#a855f7" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="following" name={t("accounts.detail.following")} stroke="#38bdf8" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="posts" name={t("accounts.detail.posts")} stroke="#34d399" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Frise chronologique + gestion warm-up */}
      <TimelineCard
        media={media}
        warmup={warmup}
        accountId={account.id}
        onWarmupChanged={async () => { await Promise.all([loadWarmup(), loadAccount()]); }}
      />

      {/* Posts */}
      <div className="bg-ink-800 border border-ink-700 rounded-xl p-6">
        <h2 className="text-lg font-semibold mb-4">{t("accounts.detail.postsTitle")} ({account.media_count})</h2>
        {media.length === 0 ? (
          <div className="py-12 text-center text-ink-500 text-sm">{t("accounts.detail.noPosts")}</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {media.map((m) => <MediaCard key={m.id} media={m} />)}
          </div>
        )}
      </div>

      {/* Notes + dernier post */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <NotesCard account={account} onSaved={loadAccount} />
        </div>
        <LastPostCard account={account} />
      </div>

      {/* Credentials + infos */}
      <div className="grid md:grid-cols-2 gap-6">
        <CredentialsCard account={account} onSaved={loadAccount} />
        <InfoCard account={account} />
      </div>
    </div>
  );
}

type StatAccent = "velvet" | "sky" | "emerald" | "amber";

function formatHours(minutes: number | null | undefined): string {
  const m = minutes ?? 0;
  if (m <= 0) return "0h";
  const h = m / 60;
  return Number.isInteger(h) ? `${h}h` : `${h.toFixed(1)}h`;
}

function DetailStatCard({
  icon: Icon, label, value, accent,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  accent: StatAccent;
}): JSX.Element {
  const accents: Record<StatAccent, string> = {
    velvet: "bg-velvet-500/15 text-velvet-300",
    sky: "bg-sky-500/15 text-sky-300",
    emerald: "bg-emerald-500/15 text-emerald-300",
    amber: "bg-amber-500/15 text-amber-300",
  };
  return (
    <div className="bg-ink-800 border border-ink-700 rounded-2xl p-5 flex items-center gap-4">
      <div className={`size-12 rounded-xl grid place-items-center shrink-0 ${accents[accent]}`}>
        <Icon className="size-6" />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-bold tabular-nums leading-none truncate">{value}</div>
        <div className="mt-1.5 text-xs text-ink-500 uppercase tracking-wider font-medium">{label}</div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <div className="text-xl font-bold tabular-nums">{value}</div>
      <div className="text-xs text-ink-500 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function MediaCard({ media }: { media: AccountMedia }): JSX.Element {
  const { t } = useTranslation();
  const [thumb, setThumb] = useState<string | null>(null);

  useEffect(() => {
    let revoked: string | null = null;
    let active = true;
    if (media.thumbnail_path) {
      void fetchMediaThumb(media.id).then((u) => {
        if (!active) { if (u) URL.revokeObjectURL(u); return; }
        revoked = u; setThumb(u);
      });
    }
    return () => { active = false; if (revoked) URL.revokeObjectURL(revoked); };
  }, [media.id, media.thumbnail_path]);

  const isVideo = media.media_type === "video" || media.media_type === "reel";

  const inner = (
    <>
      <div className="aspect-square bg-ink-900 rounded-lg overflow-hidden flex items-center justify-center relative">
        {thumb ? (
          <img src={thumb} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-ink-600 text-3xl">{isVideo ? <Play className="size-7" /> : "▦"}</span>
        )}
        {isVideo && thumb && (
          <Play className="size-5 text-white/90 absolute top-2 right-2 drop-shadow" />
        )}
      </div>
      <div className="flex items-center gap-3 mt-2 text-xs text-ink-400">
        <span className="inline-flex items-center gap-1" title={t("accounts.detail.likes")}>
          <Heart className="size-3" /> {formatCount(media.like_count)}
        </span>
        <span className="inline-flex items-center gap-1" title={t("accounts.detail.comments")}>
          <MessageCircle className="size-3" /> {formatCount(media.comment_count)}
        </span>
        {isVideo && (
          <span className="inline-flex items-center gap-1" title={t("accounts.detail.views")}>
            <Eye className="size-3" /> {formatCount(media.view_count)}
          </span>
        )}
      </div>
      {media.taken_at && (
        <div className="mt-1 text-[11px] text-ink-500 inline-flex items-center gap-1">
          <CalendarClock className="size-3" /> {formatDateTime(media.taken_at)}
        </div>
      )}
      {media.caption && <div className="text-xs text-ink-500 mt-1 line-clamp-2">{media.caption}</div>}
    </>
  );

  if (media.permalink) {
    return (
      <a href={media.permalink} target="_blank" rel="noreferrer" className="block hover:opacity-90 transition-opacity">
        {inner}
      </a>
    );
  }
  return <div>{inner}</div>;
}

function CredentialsCard({ account, onSaved }: { account: AccountDetail; onSaved: () => Promise<void> }): JSX.Element {
  const { t } = useTranslation();
  const [reveal, setReveal] = useState({ pw: false, tfa: false });
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState<AccountStatus>(account.status);
  const [saving, setSaving] = useState(false);

  async function copyCreds() {
    const line = `${account.ig_username}:${account.password ?? ""}:${account.two_fa_secret ?? ""}`;
    await navigator.clipboard.writeText(line);
  }

  async function save() {
    setSaving(true);
    try {
      await updateAccount(account.id, { status });
      setEditing(false);
      await onSaved();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : "MAJ échouée");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-ink-800 border border-ink-700 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">{t("accounts.detail.credentials")}</h2>
        {editing ? (
          <button onClick={() => void save()} disabled={saving} className="inline-flex items-center gap-1 text-sm text-velvet-400 hover:text-velvet-300 disabled:opacity-50">
            <Save className="size-4" /> {t("accounts.actions.save")}
          </button>
        ) : (
          <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-white">
            <Pencil className="size-4" /> {t("accounts.actions.edit")}
          </button>
        )}
      </div>

      <div className="space-y-3 text-sm">
        <Row label={t("accounts.columns.status")}>
          {editing ? (
            <select value={status} onChange={(e) => setStatus(e.target.value as AccountStatus)} className="px-3 py-1.5 rounded-lg bg-ink-900 border border-ink-700 text-sm">
              <option value="active">{t("status.active")}</option>
              <option value="suspended">{t("status.suspended")}</option>
              <option value="deleted">{t("status.deleted")}</option>
              <option value="pending_validation">{t("status.pending_validation")}</option>
            </select>
          ) : <StatusBadge status={account.status} />}
        </Row>
        <Row label={t("accounts.detail.password")}>
          <div className="flex items-center gap-2">
            <code className="font-mono text-xs bg-ink-900 px-2 py-1 rounded">{reveal.pw ? account.password ?? "—" : "••••••••"}</code>
            <button onClick={() => setReveal((r) => ({ ...r, pw: !r.pw }))} className="text-xs text-velvet-400 hover:text-velvet-300">
              {reveal.pw ? t("accounts.detail.hide") : t("accounts.detail.reveal")}
            </button>
          </div>
        </Row>
        <Row label={t("accounts.detail.twoFa")}>
          <div className="flex items-center gap-2">
            <code className="font-mono text-xs bg-ink-900 px-2 py-1 rounded">{reveal.tfa ? account.two_fa_secret ?? "—" : "••••••••"}</code>
            <button onClick={() => setReveal((r) => ({ ...r, tfa: !r.tfa }))} className="text-xs text-velvet-400 hover:text-velvet-300">
              {reveal.tfa ? t("accounts.detail.hide") : t("accounts.detail.reveal")}
            </button>
            <span className="text-xs text-ink-500">({account.two_fa_method})</span>
          </div>
        </Row>
      </div>

      <div className="flex justify-end mt-4">
        <button onClick={() => void copyCreds()} className="px-4 py-2 rounded-lg border border-ink-700 hover:border-ink-600 text-white text-sm font-medium">
          {t("accounts.actions.copyCreds")}
        </button>
      </div>
    </div>
  );
}

function InfoCard({ account }: { account: AccountDetail }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="bg-ink-800 border border-ink-700 rounded-xl p-6">
      <h2 className="text-lg font-semibold mb-4">{t("accounts.detail.info")}</h2>
      <div className="space-y-3 text-sm">
        <Row label={t("accounts.detail.igUserId")}><span className="font-mono text-xs">{account.ig_user_id ?? "—"}</span></Row>
        <Row label={t("accounts.columns.email")}>{account.email ?? "—"}</Row>
        <Row label={t("accounts.detail.creationIphone")}>{account.phone_name ?? "—"}</Row>
        <Row label={t("accounts.detail.proxy")}><span className="font-mono text-xs">{account.proxy_used ?? "—"}</span></Row>
        <Row label={t("accounts.columns.source")}>{t(`accounts.source.${account.source}`)}</Row>
        <Row label={t("accounts.detail.runId")}><span className="font-mono text-xs">{account.run_id ?? "—"}</span></Row>
        <Row label={t("accounts.detail.createdAt")}>{formatDateTime(account.created_at)}</Row>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 items-center">
      <span className="text-xs text-ink-500 uppercase tracking-wider font-semibold">{label}</span>
      <div>{children}</div>
    </div>
  );
}

function NotesCard({ account, onSaved }: { account: AccountDetail; onSaved: () => Promise<void> }): JSX.Element {
  const { t } = useTranslation();
  const [notes, setNotes] = useState(account.notes ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setNotes(account.notes ?? ""); }, [account.notes]);

  const dirty = notes !== (account.notes ?? "");

  async function save() {
    setSaving(true);
    try {
      await updateAccount(account.id, { notes });
      await onSaved();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : "MAJ échouée");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-ink-800 border border-ink-700 rounded-xl p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold inline-flex items-center gap-2">
          <FileText className="size-5 text-ink-500" /> {t("accounts.detail.notesTitle")}
        </h2>
        <button
          onClick={() => void save()}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-1 text-sm text-velvet-400 hover:text-velvet-300 disabled:opacity-40"
        >
          <Save className="size-4" /> {t("accounts.actions.save")}
        </button>
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={6}
        placeholder={t("accounts.detail.notesPlaceholder")}
        className="flex-1 w-full px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-sm resize-y"
      />
    </div>
  );
}

function LastPostCard({ account }: { account: AccountDetail }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="bg-ink-800 border border-ink-700 rounded-xl p-6 h-full flex flex-col">
      <h2 className="text-lg font-semibold inline-flex items-center gap-2 mb-4">
        <CalendarClock className="size-5 text-ink-500" /> {t("accounts.detail.lastPostTitle")}
      </h2>
      {account.last_post_at ? (
        <div className="flex-1 flex flex-col justify-center">
          <div className="text-2xl font-bold">{formatDateTime(account.last_post_at)}</div>
          <div className="text-sm text-ink-500 mt-1">{relativeTime(account.last_post_at)}</div>
        </div>
      ) : (
        <div className="flex-1 flex items-center text-ink-500 text-sm">{t("accounts.detail.noPosts")}</div>
      )}
    </div>
  );
}

// Événement de frise : un post ou une session de warm-up, normalisés sur `at`.
type TimelineEvent =
  | { kind: "post"; at: string; media: AccountMedia }
  | { kind: "warmup"; at: string; session: WarmupSession };

function TimelineCard({
  media, warmup, accountId, onWarmupChanged,
}: {
  media: AccountMedia[];
  warmup: WarmupSession[];
  accountId: string;
  onWarmupChanged: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  const [startedAt, setStartedAt] = useState("");
  const [minutes, setMinutes] = useState("");
  const [note, setNote] = useState("");
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const events = useMemo<TimelineEvent[]>(() => {
    const out: TimelineEvent[] = [];
    for (const m of media) {
      if (m.taken_at) out.push({ kind: "post", at: m.taken_at, media: m });
    }
    for (const w of warmup) {
      out.push({ kind: "warmup", at: w.started_at, session: w });
    }
    return out.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [media, warmup]);

  async function addWarmup() {
    setErr(null);
    const when = startedAt ? new Date(startedAt) : null;
    const min = parseInt(minutes, 10);
    if (!when || Number.isNaN(when.getTime())) { setErr(t("accounts.detail.warmup.errStart")); return; }
    if (!Number.isInteger(min) || min <= 0) { setErr(t("accounts.detail.warmup.errDuration")); return; }
    setAdding(true);
    try {
      await createWarmup(accountId, { started_at: when.toISOString(), duration_minutes: min, note: note || undefined });
      setStartedAt(""); setMinutes(""); setNote("");
      await onWarmupChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : t("accounts.detail.warmup.errAdd"));
    } finally {
      setAdding(false);
    }
  }

  async function removeWarmup(warmupId: string) {
    try {
      await deleteWarmup(accountId, warmupId);
      await onWarmupChanged();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : t("accounts.detail.warmup.errDelete"));
    }
  }

  return (
    <div className="bg-ink-800 border border-ink-700 rounded-xl p-6">
      <h2 className="text-lg font-semibold inline-flex items-center gap-2 mb-4">
        <Clock className="size-5 text-ink-500" /> {t("accounts.detail.timeline.title")}
      </h2>

      {/* Ajout d'une session de warm-up */}
      <div className="rounded-lg border border-ink-700 bg-ink-900/60 p-4 mb-5">
        <div className="text-xs text-ink-500 uppercase tracking-wider font-semibold mb-3">
          {t("accounts.detail.warmup.addTitle")}
        </div>
        {err && <div className="mb-3 text-xs text-red-300">{err}</div>}
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="text-xs text-ink-500">{t("accounts.detail.warmup.start")}</span>
            <input
              type="datetime-local"
              value={startedAt}
              onChange={(e) => setStartedAt(e.target.value)}
              className="mt-1 block px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs text-ink-500">{t("accounts.detail.warmup.duration")}</span>
            <input
              type="number"
              min={1}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              placeholder="30"
              className="mt-1 block w-28 px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-sm"
            />
          </label>
          <label className="block flex-1 min-w-[160px]">
            <span className="text-xs text-ink-500">{t("accounts.detail.warmup.note")}</span>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="mt-1 block w-full px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-sm"
            />
          </label>
          <button
            onClick={() => void addWarmup()}
            disabled={adding}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 text-white text-sm font-medium disabled:opacity-50"
          >
            <Plus className="size-4" /> {t("accounts.detail.warmup.add")}
          </button>
        </div>
      </div>

      {/* Frise */}
      {events.length === 0 ? (
        <div className="py-10 text-center text-ink-500 text-sm">{t("accounts.detail.timeline.empty")}</div>
      ) : (
        <ol className="relative border-l border-ink-700 ml-3 space-y-5">
          {events.map((ev) => (
            <li key={ev.kind === "post" ? `p-${ev.media.id}` : `w-${ev.session.id}`} className="ml-5">
              <span
                className={`absolute -left-[7px] mt-1.5 size-3.5 rounded-full ring-4 ring-ink-800 ${ev.kind === "warmup" ? "bg-amber-400" : "bg-velvet-500"}`}
              />
              <div className="text-xs text-ink-500">{formatDateTime(ev.at)}</div>
              {ev.kind === "warmup" ? (
                <div className="flex items-start justify-between gap-3">
                  <div className="text-sm">
                    <span className="inline-flex items-center gap-1 text-amber-300 font-medium">
                      <Flame className="size-3.5" /> {t("accounts.detail.timeline.warmupLabel", { min: ev.session.duration_minutes })}
                    </span>
                    {ev.session.note && <div className="text-ink-400 mt-0.5 whitespace-pre-wrap">{ev.session.note}</div>}
                  </div>
                  <button
                    onClick={() => void removeWarmup(ev.session.id)}
                    className="text-ink-500 hover:text-red-300 shrink-0"
                    title={t("accounts.detail.warmup.delete")}
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              ) : (
                <div className="text-sm">
                  <span className="inline-flex items-center gap-1 text-velvet-300 font-medium">
                    <ImageIcon className="size-3.5" /> {t(`accounts.detail.timeline.postLabel.${ev.media.media_type}`)}
                  </span>
                  <span className="text-ink-400 ml-2 inline-flex items-center gap-3">
                    <span className="inline-flex items-center gap-1"><Heart className="size-3" /> {formatCount(ev.media.like_count)}</span>
                    {(ev.media.media_type === "video" || ev.media.media_type === "reel") && (
                      <span className="inline-flex items-center gap-1"><Eye className="size-3" /> {formatCount(ev.media.view_count)}</span>
                    )}
                  </span>
                  {ev.media.caption && <div className="text-ink-500 mt-0.5 line-clamp-1">{ev.media.caption}</div>}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
